import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { useSiteLogo } from '../hooks/useSiteLogo';

export default function RegisterPage() {
  const COLORED_LOGO = useSiteLogo();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    department: '',
    job_title: '',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    try {
      const { confirmPassword, ...userData } = formData;
      const user = await register(userData);
      toast.success(`Welcome, ${user.first_name}! Your account has been created.`);
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/intranet');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-12" data-testid="register-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-neutral-200">
          <div className="text-center mb-8">
            <Link to="/">
              <img src={COLORED_LOGO} alt="NSEC" className="h-16 mx-auto mb-6" />
            </Link>
            <h1 className="font-outfit font-bold text-2xl text-neutral-900">Create Account</h1>
            <p className="text-neutral-600 mt-2">Register for employee intranet access</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-neutral-700">First Name *</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="bg-neutral-50 border-neutral-200"
                  data-testid="register-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-neutral-700">Last Name *</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="bg-neutral-50 border-neutral-200"
                  data-testid="register-last-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-neutral-700">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-neutral-50 border-neutral-200"
                placeholder="your.email@nsecltd.co.uk"
                data-testid="register-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="department" className="text-neutral-700">Department</Label>
                <Input
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  className="bg-neutral-50 border-neutral-200"
                  placeholder="e.g. Operations"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="job_title" className="text-neutral-700">Job Title</Label>
                <Input
                  id="job_title"
                  name="job_title"
                  value={formData.job_title}
                  onChange={handleChange}
                  className="bg-neutral-50 border-neutral-200"
                  placeholder="e.g. Driver"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-neutral-700">Phone</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="bg-neutral-50 border-neutral-200"
                placeholder="07xxx xxxxxx"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-neutral-700">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="bg-neutral-50 border-neutral-200 pr-10"
                  placeholder="Min 6 characters"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-neutral-700">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="bg-neutral-50 border-neutral-200"
                placeholder="Re-enter password"
                data-testid="register-confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-lime-600 hover:bg-lime-700 text-white font-semibold py-6 rounded-full"
              data-testid="register-submit"
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-neutral-600 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-lime-600 hover:underline font-medium">
                Sign In
              </Link>
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-neutral-200 text-center">
            <Link to="/" className="text-neutral-500 text-sm hover:text-neutral-700">
              ← Back to main website
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
